/**
 * Shared teaching-app profiles.
 * MorningReport Cases, FactPattern, and RadioCall share one ecosystem.
 * Each profile supplies host, voices, step labels, collections, reviewer, and
 * countdown appearance. Do not copy schools / seats / grants / progress.
 */

/** @typedef {"resuscitation" | "lectern" | "flightdeck"} CountdownAppearance */

/**
 * @typedef {object} TeachingStepLabels
 * @property {string} set - Graded set step (issues / failures / differential)
 * @property {string} choice - Graded discriminating-choice step
 * @property {string} commit - Final commitment step
 * @property {string} close - Teaching close
 */

/**
 * @typedef {object} TeachingAppProfile
 * @property {string} id
 * @property {string} displayName
 * @property {string} facultyDisplayName
 * @property {string} tagline
 * @property {string} bannerText
 * @property {string} route
 * @property {string} facultyRoute
 * @property {string} sessionCollection
 * @property {string} casesCollection
 * @property {string} caseKeysCollection
 * @property {"legalReviewer" | "cfi" | "msi3Faculty"} reviewerRole
 * @property {CountdownAppearance} countdownAppearance
 * @property {{ left: string, right: string }} voices
 * @property {TeachingStepLabels} stepLabels
 * @property {string[]} outlineTopics
 * @property {string|null} [topicCatalogSlug] LegalKB / SpecialtyKB catalog slug for faculty picker
 * @property {string} [defaultJurisdiction] e.g. federal-ube for FactPattern
 * @property {{ specialty: string, titles: string[] }} freeCases
 * @property {"msi3" | "asi"} hostFamily
 */

/** @type {Readonly<TeachingAppProfile>} */
export const MORNING_REPORT_PROFILE = Object.freeze({
  id: "clinicalHud5",
  displayName: "MorningReport Cases",
  facultyDisplayName: "MorningReport Cases (Faculty)",
  tagline: "Powered by Medical SuperIntelligence™",
  bannerText:
    "MorningReport Cases — listen, then name the differential. Simulated case. Do not enter real patient information.",
  route: "/clinical-hud-5",
  facultyRoute: "/msi3/faculty/cases",
  sessionCollection: "clinicalHud5Teaching",
  casesCollection: "msi3Cases",
  caseKeysCollection: "msi3CaseKeys",
  reviewerRole: "msi3Faculty",
  countdownAppearance: "resuscitation",
  voices: Object.freeze({ left: "Alnilam", right: "Aoede" }),
  stepLabels: Object.freeze({
    set: "Differential",
    choice: "Discriminating tests",
    commit: "Diagnosis",
    close: "Teaching close",
  }),
  outlineTopics: Object.freeze([
    "Emergency Medicine",
  ]),
  freeCases: Object.freeze({
    specialty: "Emergency Medicine",
    titles: Object.freeze([
      "Free EM case 1",
      "Free EM case 2",
      "Free EM case 3",
    ]),
  }),
  hostFamily: "msi3",
});

/** @type {Readonly<TeachingAppProfile>} */
export const FACT_PATTERN_PROFILE = Object.freeze({
  id: "factPattern",
  displayName: "FactPattern",
  facultyDisplayName: "FactPattern (Faculty)",
  tagline: "Powered by Applied SuperIntelligence™",
  bannerText:
    "FactPattern — listen, then name the issues. Simulated fact pattern. Do not enter real client information.",
  route: "/fact-pattern",
  facultyRoute: "/fact-pattern/faculty/cases",
  sessionCollection: "factPatternTeaching",
  casesCollection: "factPatternCases",
  caseKeysCollection: "factPatternCaseKeys",
  reviewerRole: "legalReviewer",
  countdownAppearance: "lectern",
  voices: Object.freeze({ left: "Alnilam", right: "Aoede" }),
  stepLabels: Object.freeze({
    set: "Issues",
    choice: "Facts and documents",
    commit: "Theory",
    close: "Teaching close",
  }),
  outlineTopics: Object.freeze([
    "Torts — Negligence",
    "Torts — Intentional torts",
    "Torts — Strict liability",
    "Torts — Defenses",
    "Contracts — Formation",
    "Contracts — Defenses to formation",
    "Contracts — Breach and remedies",
  ]),
  topicCatalogSlug: "law-mbe",
  defaultJurisdiction: "federal-ube",
  freeCases: Object.freeze({
    specialty: "Law",
    titles: Object.freeze([
      "Free Torts case",
      "Free Contracts case",
      "Free mixed Torts and Contracts case",
    ]),
  }),
  hostFamily: "asi",
});

/** @type {Readonly<TeachingAppProfile>} */
export const RADIO_CALL_PROFILE = Object.freeze({
  id: "radioCall",
  displayName: "RadioCall",
  facultyDisplayName: "RadioCall (Faculty)",
  tagline: "Powered by Applied SuperIntelligence™",
  bannerText:
    "RadioCall — listen, then name the failures. Simulated radio call. Do not enter real flight information.",
  route: "/radio-call",
  facultyRoute: "/radio-call/faculty/cases",
  sessionCollection: "radioCallTeaching",
  casesCollection: "radioCallCases",
  caseKeysCollection: "radioCallCaseKeys",
  reviewerRole: "cfi",
  countdownAppearance: "flightdeck",
  voices: Object.freeze({ left: "Alnilam", right: "Aoede" }),
  stepLabels: Object.freeze({
    set: "Possible failures",
    choice: "Checks and instruments",
    commit: "Decision now",
    close: "Teaching close",
  }),
  outlineTopics: Object.freeze([
    "Powerplant",
    "Electrical",
    "Weather and icing",
    "Instrument failures",
    "Communications failure",
  ]),
  freeCases: Object.freeze({
    specialty: "Aviation",
    titles: Object.freeze([
      "Free powerplant case",
      "Free weather case",
      "Free instrument or electrical case",
    ]),
  }),
  hostFamily: "asi",
});

/** @type {readonly TeachingAppProfile[]} */
export const TEACHING_APP_PROFILES = Object.freeze([
  MORNING_REPORT_PROFILE,
  FACT_PATTERN_PROFILE,
  RADIO_CALL_PROFILE,
]);

/**
 * @param {string} id
 * @returns {TeachingAppProfile | undefined}
 */
export function getTeachingAppProfile(id) {
  return TEACHING_APP_PROFILES.find((p) => p.id === id);
}

/**
 * Profiles that appear as live tiles on the Applied Superintelligence launcher.
 * @returns {TeachingAppProfile[]}
 */
export function getAsiTeachingProfiles() {
  return TEACHING_APP_PROFILES.filter((p) => p.hostFamily === "asi");
}
